import {
  Drawer,
  Button,
  Table,
  Row,
  Col,
  Space,
  Divider,
  Upload,
  Card,
  Input,
  Form,
  Tag,
  Alert,
} from "antd";
import {
  DeleteOutlined,
  InboxOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useForm } from "antd/lib/form/Form";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../shared/types/common";
import { showNotification } from "../../../shared/utils/utils";
import {
  batchUploadCandidates,
  uploadCandidate,
} from "../../../core/store/reducers/candidate-screening.slice";

interface Props {
  onClose: () => void;
  visible: boolean;
  onSubmit?: () => void;
}

// Resume files are PII (plan.md §10): only text extracted server-side is sent to
// the AI provider — raw files go to the existing S3 bucket, never to the LLM.
const ACCEPTED = ".pdf,.doc,.docx";
const ACCEPTED_MIME = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const UploadCandidatesComponent = ({
  onClose,
  visible,
  onSubmit = () => {
    return;
  },
}: Props) => {
  const dispatch = useDispatch();
  const [form] = useForm();
  const { screenType } = useSelector((state: RootState) => state.settings);
  const [fileList, setFileList] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  // Per-file outcome from a batch upload (uploaded / duplicate / skipped+reason).
  const [uploadResults, setUploadResults] = useState<any[]>([]);

  const isSingle = fileList.length === 1;

  const handleAddFile = (info: any) => {
    const file: File = info.file;
    if (!file) return;
    const okType =
      ACCEPTED_MIME.includes(file.type) ||
      /\.(pdf|docx?|)$/i.test(file.name);
    if (!okType) {
      showNotification("Only PDF or DOCX resumes are allowed.", "error");
      return;
    }
    setFileList((prev) => {
      // De-dupe by name + size so the same resume isn't queued twice.
      const exists = prev.some(
        (f) => f.name === file.name && f.size === file.size
      );
      return exists ? prev : [...prev, file];
    });
  };

  const handleDeleteFile = (index: number) => {
    setFileList((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      showNotification("Please add at least one resume file.", "warning");
      return;
    }
    setUploading(true);
    setUploadResults([]);

    if (isSingle) {
      const values = form.getFieldsValue();
      const formData = new FormData();
      formData.append("file", fileList[0]);
      if (values?.fullName) formData.append("fullName", values.fullName);
      if (values?.email) formData.append("email", values.email);
      if (values?.phone) formData.append("phone", values.phone);
      dispatch(uploadCandidate(formData)).then((val: any) => {
        setUploading(false);
        const payload = val?.payload;
        // Success path.
        if (payload?.data?.data && payload?.status !== 400) {
          const isDuplicate = payload?.data?.data?.duplicate;
          showNotification(
            payload?.data?.message || "Resume uploaded successfully.",
            isDuplicate ? "warning" : "success"
          );
          resetAndSubmit();
          return;
        }
        // Rejected (e.g. scanned / image-only PDF) — surface the reason, keep
        // the file queued so the recruiter can swap it out.
        const msg =
          payload?.data?.message ||
          payload?.message ||
          "The resume could not be processed. Please try a text-based PDF/DOCX.";
        showNotification(msg, "error");
      });
    } else {
      const formData = new FormData();
      fileList.forEach((file) => formData.append("files", file));
      dispatch(batchUploadCandidates(formData)).then((val: any) => {
        setUploading(false);
        const data = val?.payload?.data?.data;
        if (data) {
          setUploadResults(data.results || []);
          const summary =
            val?.payload?.data?.message ||
            `${data.uploaded ?? 0} uploaded, ${data.duplicates ?? 0} duplicate(s)` +
              (data.skipped ? `, ${data.skipped} unreadable/failed` : "");
          showNotification(
            summary,
            data.skipped ? "warning" : "success"
          );
          setFileList([]);
          form.resetFields();
          onSubmit();
        }
      });
    }
  };

  const resetAndSubmit = () => {
    setFileList([]);
    setUploadResults([]);
    form.resetFields();
    onSubmit();
  };

  const columns = [
    { title: "File Name", dataIndex: "name", key: "name" },
    {
      title: "Size",
      dataIndex: "size",
      key: "size",
      render: (size: number) => `${(size / 1024).toFixed(1)} KB`,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, __: any, index: number) => (
        <Space>
          <Button
            type="link"
            onClick={() => handleDeleteFile(index)}
            icon={<DeleteOutlined style={{ color: "#FF0000" }} />}
          />
        </Space>
      ),
    },
  ];

  return (
    <Drawer
      maskClosable={false}
      title={<strong>Upload Candidate Resumes</strong>}
      className="form-drawer"
      width={
        screenType.isPortrait
          ? "90%"
          : screenType.isLg || screenType.isXl
          ? 720
          : 500
      }
      onClose={onClose}
      open={visible}
    >
      <Card
        style={{
          borderRadius: 8,
          backgroundColor: "#e6f7ff",
          marginBottom: 16,
        }}
      >
        <p style={{ fontSize: 13, marginBottom: 4 }}>
          <ExclamationCircleOutlined /> Accepted formats: PDF, DOC, DOCX.
        </p>
        <p style={{ fontSize: 12, marginBottom: 0 }}>
          Add a single resume to enter candidate details, or add several to
          upload them as a batch. Duplicate resumes are detected on the server
          by content hash.
        </p>
      </Card>

      <Upload.Dragger
        name="file"
        multiple={true}
        showUploadList={false}
        customRequest={handleAddFile}
        accept={ACCEPTED}
        style={{ padding: "16px 0" }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          Click or drag resume files to this area
        </p>
      </Upload.Dragger>

      {fileList.length > 0 && (
        <Table
          className="mt20"
          rowKey={(record: any, index?: number) => `${record.name}-${index}`}
          columns={columns}
          dataSource={fileList}
          pagination={false}
          size="small"
          style={{ marginTop: 16 }}
        />
      )}

      {uploadResults.length > 0 && (
        <>
          <Divider>Upload Results</Divider>
          {uploadResults.some((r) => r.status === "skipped") && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Some files could not be read"
              description="Scanned or image-only PDFs have no extractable text. Re-export them as a text-based PDF/DOCX and upload again."
            />
          )}
          <Table
            rowKey={(record: any, index?: number) =>
              `${record.fileName}-${index}`
            }
            columns={[
              { title: "File Name", dataIndex: "fileName", key: "fileName" },
              {
                title: "Status",
                dataIndex: "status",
                key: "status",
                render: (status: string) => {
                  const color =
                    status === "uploaded"
                      ? "green"
                      : status === "duplicate"
                      ? "gold"
                      : "red";
                  return <Tag color={color}>{status}</Tag>;
                },
              },
              {
                title: "Note",
                dataIndex: "reason",
                key: "reason",
                render: (reason: string) => reason || "—",
              },
            ]}
            dataSource={uploadResults}
            pagination={false}
            size="small"
          />
        </>
      )}

      {isSingle && (
        <>
          <Divider>Candidate Details (optional)</Divider>
          <Form form={form} layout="vertical" name="single-candidate">
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="fullName" label="Full Name">
                  <Input allowClear placeholder="Extracted from resume if blank" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="email" label="Email">
                  <Input allowClear placeholder="candidate@example.com" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="phone" label="Phone">
                  <Input allowClear placeholder="Phone" />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </>
      )}

      <Row justify="end" style={{ marginTop: 16 }}>
        <Col>
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              loading={uploading}
              onClick={handleUpload}
              disabled={fileList.length === 0}
            >
              {isSingle
                ? "Upload Resume"
                : `Upload ${fileList.length} Resumes`}
            </Button>
          </Space>
        </Col>
      </Row>
    </Drawer>
  );
};

export default UploadCandidatesComponent;
